import { useVirtualizer } from '@tanstack/react-virtual';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import React, { useCallback, useMemo, useRef } from 'react';
import styles from '../GanttChart.module.scss';
import type { HeaderDatePreview } from '../gantt-internal-types';
import type { GanttZoomLevel } from '../gantt-zoom';

interface Props {
  zoom: GanttZoomLevel;
  dayColumns: Array<DateTime>;
  scrollLeft: number;
  width: number;
  todayIndex: number;
  previewDates: HeaderDatePreview | null;
}

const GanttChartDayGrid: React.FC<Props> = ({
  zoom,
  dayColumns,
  scrollLeft,
  width,
  todayIndex,
  ...props
}) => {
  const parentRef = useRef<HTMLDivElement>(null);

  const columnVirtualizer = useVirtualizer({
    horizontal: true,
    count: dayColumns.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => zoom.columnWidth,
  });

  React.useLayoutEffect(() => {
    columnVirtualizer.scrollToOffset(scrollLeft);
    // biome-ignore lint/correctness/useExhaustiveDependencies: intentional
  }, [scrollLeft]);

  // Memoize preview dates as Date objects
  const previewDates = useMemo(() => {
    if (props.previewDates == null) {
      return null;
    }
    return {
      startDate: DateTime.fromISO(props.previewDates.startDate),
      endDate: DateTime.fromISO(props.previewDates.endDate),
      originalStartDate:
        props.previewDates.originalStartDate != null
          ? DateTime.fromISO(props.previewDates.originalStartDate)
          : null,
      originalEndDate:
        props.previewDates.originalEndDate != null
          ? DateTime.fromISO(props.previewDates.originalEndDate)
          : null,
    };
  }, [props.previewDates]);

  const renderCell = useCallback(
    ({ columnIndex, key }: { columnIndex: number; key: React.Key }) => {
      const date = dayColumns[columnIndex];

      // Calcualate if this cell should have preview background (from dragging/resizing tasks)
      const datePreview =
        previewDates != null && date >= previewDates.startDate && date <= previewDates.endDate;
      const datePreviewStart = datePreview && date.hasSame(previewDates!.startDate, 'day');
      const datePreviewEnd = datePreview && date.hasSame(previewDates!.endDate, 'day');

      const datePreviewOriginal =
        previewDates != null &&
        previewDates.originalStartDate != null &&
        previewDates.originalEndDate != null &&
        date >= previewDates.originalStartDate &&
        date <= previewDates.originalEndDate;
      const datePreviewOriginalStart =
        datePreviewOriginal && date.hasSame(previewDates!.originalStartDate!, 'day');
      const datePreviewOriginalEnd =
        datePreviewOriginal && date.hasSame(previewDates!.originalEndDate!, 'day');

      return (
        <div
          className={styles.topHeaderDayCell}
          key={key}
          style={{ paddingRight: zoom.dayPadding }}
        >
          {datePreviewOriginal && (
            <div
              className={classNames(styles.datePreview, styles.datePreviewOriginal, {
                [styles.datePreviewStart]: datePreviewOriginalStart,
                [styles.datePreviewEnd]: datePreviewOriginalEnd,
              })}
            />
          )}
          {datePreview && (
            <>
              <div
                className={classNames(styles.datePreview, {
                  [styles.datePreviewStart]: datePreviewStart,
                  [styles.datePreviewEnd]: datePreviewEnd,
                })}
              />
            </>
          )}
          <div
            className={classNames(styles.label, {
              [styles.today]: columnIndex === todayIndex,
              [styles.datePreviewText]: datePreview,
              [styles.datePreviewOriginalText]: datePreview,
            })}
          >
            {zoom.showDayOfWeek && (
              <span className={styles.weekDay}>{date.toFormat('EEEEE').toUpperCase()}</span>
            )}
            <span
              className={classNames(styles.date, {
                [styles.workday]: date.weekday < 6,
                [styles.tiny]: zoom.smallDateFontSize,
              })}
            >
              {date.day}
            </span>
          </div>
        </div>
      );
    },
    [dayColumns, todayIndex, previewDates, zoom]
  );

  return (
    <div className={styles.topHeaderRow} ref={parentRef}>
      <div
        style={{
          width: columnVirtualizer.getTotalSize(),
          height: '100%',
          position: 'relative',
        }}
      >
        {columnVirtualizer.getVirtualItems().map(virtualColumn => (
          <div
            key={virtualColumn.key}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              transform: `translateX(${virtualColumn.start}px)`,
            }}
          >
            <div
              style={{
                width: zoom.columnWidth,
                height: 24,
                overflow: 'hidden',
              }}
            >
              {renderCell({
                columnIndex: virtualColumn.index,
                key: virtualColumn.key,
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
export default GanttChartDayGrid;
